import { all, one } from '../lib/dom';

/**
 * Contact form.
 *
 * Replaces Gravity Forms' client side (8 jQuery-dependent scripts). The markup is
 * left as WordPress rendered it and progressively enhanced, so the existing
 * stylesheet keeps working — errors reuse GF's own `.gfield_error` /
 * `.validation_message` classes, which theme.min.css already styles red.
 *
 * ── Submission is deliberately not wired up ────────────────────────────────────
 * The original form POSTed to WordPress, so it stopped working the moment this
 * became a static site. Rather than guess at a replacement, the destination is
 * read from `data-endpoint` on the <form>:
 *
 *     <form id="gform_9" data-endpoint="https://…">
 *
 * With no endpoint set the form validates but refuses to submit, and says so.
 * It never reports success for a message that went nowhere.
 */

const REQUIRED_MESSAGE = 'This field is required.';
const EMAIL_MESSAGE = 'Please enter a valid email address.';
const NO_ENDPOINT_MESSAGE =
  'This form is not connected to a mail service yet, so your message was not sent. ' +
  'Please call 218-348-3625 or email us directly in the meantime.';
const FAILURE_MESSAGE =
  'Something went wrong sending your message. Please try again, or call 218-348-3625.';
const SUCCESS_MESSAGE = 'Thanks — your message has been sent. We will be in touch shortly.';

type FieldInput = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

export function contactForm(): void {
  for (const form of all<HTMLFormElement>('form[id^="gform_"]')) {
    enhance(form);
  }
}

function enhance(form: HTMLFormElement): void {
  // The rendered button carries inline onclick/onkeypress handlers that call
  // jQuery, which no longer exists. Strip them before they throw.
  for (const button of all<HTMLElement>('[onclick], [onkeypress]', form)) {
    button.removeAttribute('onclick');
    button.removeAttribute('onkeypress');
  }

  // Browser-native bubbles would fight our own messages.
  form.setAttribute('novalidate', '');

  let validated = false;

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    validated = true;

    clearFormMessage(form);

    const invalid = validateAll(form);
    if (invalid.length > 0) {
      invalid[0]?.focus();
      return;
    }

    if (isHoneypotFilled(form)) {
      // Silently accept and discard: telling a bot it was caught helps it adapt.
      showFormMessage(form, SUCCESS_MESSAGE, 'success');
      form.reset();
      return;
    }

    void submit(form);
  });

  // Re-check as the visitor fixes things, but only after a first failed attempt —
  // flagging a field the moment it is touched is hostile.
  for (const input of fieldsOf(form)) {
    const recheck = (): void => {
      if (validated) validateField(input);
    };
    input.addEventListener('blur', recheck);
    input.addEventListener('input', recheck);
  }
}

function fieldsOf(form: HTMLFormElement): FieldInput[] {
  return all<FieldInput>('input, textarea, select', form).filter((input) => {
    if (input instanceof HTMLInputElement && input.type === 'hidden') return false;
    // Honeypots are validated separately and must stay invisible to real users.
    return !input.closest('.gfield--type-honeypot, .gform_validation_container');
  });
}

function validateAll(form: HTMLFormElement): FieldInput[] {
  return fieldsOf(form).filter((input) => !validateField(input));
}

/** Returns true when the field is acceptable. */
function validateField(input: FieldInput): boolean {
  const field = input.closest<HTMLElement>('.gfield');
  if (!field) return true;

  const value = input.value.trim();
  const required = input.getAttribute('aria-required') === 'true' || input.required;

  let error: string | null = null;
  if (required && value === '') {
    error = REQUIRED_MESSAGE;
  } else if (
    value !== '' &&
    input instanceof HTMLInputElement &&
    input.type === 'email' &&
    !isEmail(value)
  ) {
    error = EMAIL_MESSAGE;
  }

  setFieldError(field, input, error);
  return error === null;
}

/**
 * Deliberately loose: something before an @, something after, and a dot in the
 * domain. Stricter patterns reject valid addresses, and only sending mail proves
 * an address is real anyway.
 */
function isEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function setFieldError(field: HTMLElement, input: FieldInput, error: string | null): void {
  const id = `${input.id || input.name}-error`;
  let message = one<HTMLElement>(`#${CSS.escape(id)}`, field);

  if (error === null) {
    field.classList.remove('gfield_error');
    input.removeAttribute('aria-invalid');
    input.removeAttribute('aria-describedby');
    message?.remove();
    return;
  }

  field.classList.add('gfield_error');
  input.setAttribute('aria-invalid', 'true');
  input.setAttribute('aria-describedby', id);

  if (!message) {
    message = document.createElement('div');
    message.id = id;
    message.className = 'gfield_description validation_message';
    field.append(message);
  }
  message.textContent = error;
}

function isHoneypotFilled(form: HTMLFormElement): boolean {
  return all<FieldInput>(
    '.gfield--type-honeypot input, .gform_validation_container input',
    form,
  ).some((input) => input.value.trim() !== '');
}

async function submit(form: HTMLFormElement): Promise<void> {
  const endpoint = form.dataset['endpoint'];

  if (!endpoint) {
    // No backend configured. Say so plainly rather than pretending to send.
    showFormMessage(form, NO_ENDPOINT_MESSAGE, 'error');
    return;
  }

  const button = one<HTMLButtonElement>('button[type="submit"]', form);
  const label = button?.textContent ?? '';
  if (button) {
    button.disabled = true;
    button.textContent = 'Sending…';
  }

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form),
    });
    if (!response.ok) throw new Error(`Endpoint responded ${response.status}`);

    showFormMessage(form, SUCCESS_MESSAGE, 'success');
    form.reset();
    for (const field of all('.gfield.has_value', form)) field.classList.remove('has_value');
  } catch (cause) {
    console.error('[contact form] submission failed', cause);
    showFormMessage(form, FAILURE_MESSAGE, 'error');
  } finally {
    if (button) {
      button.disabled = false;
      button.textContent = label;
    }
  }
}

function showFormMessage(form: HTMLFormElement, text: string, kind: 'success' | 'error'): void {
  let box = one<HTMLElement>('.dd-form-message', form);
  if (!box) {
    box = document.createElement('div');
    box.className = 'dd-form-message';
    // Announce without stealing focus from wherever the visitor is.
    box.setAttribute('role', 'status');
    box.setAttribute('aria-live', 'polite');
    form.prepend(box);
  }
  box.classList.toggle('gform_validation_errors', kind === 'error');
  box.textContent = text;
  box.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
}

function clearFormMessage(form: HTMLFormElement): void {
  one('.dd-form-message', form)?.remove();
}
